import React, { useCallback, useEffect, useState } from "react";
import { m as motion } from "framer-motion";
import FocusLock from "react-focus-lock";
import MessageBoard from "@/components/board";
import Button from "@/components/button";
import Messenger from "@/components/messenger";
import { useTheme } from "@/contexts/theme";
import CloseIcon from "@/icons/close";
import { readMessage } from "@/remote/api";

const BOARD_REFRESH_SECONDS: number = parseInt(
  process.env.GATSBY_BOARD_REFRESH_SECONDS ?? "2",
  10,
);

const Board: React.FC = () => {
  const { showBoard, message, setMessage } = useTheme();

  const [ready, setReady] = useState<boolean | Date>(false);

  const [timeout, setTimeoutRef] = useState<number | undefined>();

  const refreshBoard = useCallback(() => {
    readMessage()
      .then((data) => {
        setMessage(data?.message);

        if (data?.ready) {
          setReady(true);
          return;
        }

        if (data?.ready_at) {
          setReady(new Date(data.ready_at));
          return;
        }

        setReady(false);
      })
      .finally(() => {
        setTimeoutRef(
          window.setInterval(
            () => refreshBoard(),
            BOARD_REFRESH_SECONDS * 1000,
          ),
        );
      });
  }, [setMessage]);

  useEffect(() => () => window.clearInterval(timeout), [timeout]);

  useEffect(() => refreshBoard(), [refreshBoard]);

  useEffect(() => {
    const opts: AddEventListenerOptions & EventListenerOptions = {
      passive: true,
    };

    const closeOnEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape") showBoard(false);
    };

    document.addEventListener("keyup", closeOnEscape, opts);

    return () => document.removeEventListener("keyup", closeOnEscape, opts);
  }, [showBoard]);

  return (
    <FocusLock>
      <div className="fixed inset-0 z-50 flex flex-col items-stretch justify-start overflow-hidden pt-xl text-primary supports-p3:text-primary-p3">
        <motion.div
          className="fixed top-0 right-0 z-40 p-gutter"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: 10, transitionEnd: { display: "none" } }}
        >
          <Button
            size="sm"
            type="secondary"
            className="text-primary supports-p3:text-primary-p3"
            icon={CloseIcon}
            onClick={() => showBoard(false)}
          >
            Schließen
          </Button>
        </motion.div>

        <motion.div
          className="absolute inset-0 z-0 bg-mirage/90 backdrop-blur-3xl supports-p3:bg-mirage-p3/90"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { delay: 0.6 } }}
        />

        <div className="relative z-20 flex flex-col items-center justify-center flex-1">
          <motion.h2
            className="mt-0 mb-base font-medium text-3xl-fluid/[0.9] font-display xs:text-4xl-fluid/[0.9] lg:text-3xl-fluid/[0.9]"
            initial={{ opacity: 0, y: 30, rotate: 2, transformOrigin: "left" }}
            animate={{ opacity: 1, y: 5, rotate: -4 }}
            exit={{ opacity: 0, y: -30, rotate: -9 }}
            transition={{
              rotate: { type: "spring", bounce: 0.6 },
              opacity: { ease: "easeOut" },
            }}
          >
            Schick uns <br />
            was Analoges
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ ease: "easeOut" }}
          >
            <MessageBoard message={message ?? null} />
          </motion.div>
          <div className="flex flex-col justify-center w-full">
            <Messenger id="global-messenger" ready={ready} />
          </div>
        </div>
      </div>
    </FocusLock>
  );
};

export default Board;
